import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GOODS_STUDIO_DEFAULT_PARAMS } from '@/pages/ipe/workbench/constants/aiStudioConfig';
import { navigateWorkbenchWithState } from '@/pages/ipe/workbench/constants/workbenchNavConfig';
import { createAiGoodsTaskFromDetailSources } from '@/pages/ipe/workbench/utils/createAiGoodsTaskFromDetailSources';
import { notifyQuickBridgeTaskCreated } from '@/pages/ipe/workbench/utils/showTaskCreatedDetailToast';
import { remakeGoodsStudioGroupInPlace } from '@/pages/ipe/workbench/utils/goodsStudioRemakeInPlace';
import {
  buildPersistedGoodsPrintUrlMap,
  inferCropTaskIdFromImageIds,
  persistGoodsPrintUrlsToSession,
} from '@/pages/ipe/workbench/utils/goodsPrintUrlResolve';
import i18n from '@/i18n';
/**
 * 详情页联动「主图创作」：打开参数弹窗 → 确认后创建任务或带入主图创作页。
 * 来自商品设计 / AI文案时确认后直接提交生成，不再跳转主图创作页手动点「立即生成」。
 * @param {{ setToast?: (payload: { message: string, type?: string }) => void, onRemakeStarted?: (payload: { taskId: string, groupIndex: number }) => void }} [options]
 */
export default function useDetailGoodsTemplateBridge({ setToast, onRemakeStarted } = {}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [studioParams, setStudioParams] = useState(() => ({ ...GOODS_STUDIO_DEFAULT_PARAMS }));
  const [pendingSources, setPendingSources] = useState([]);
  const [pendingFromCropTaskId, setPendingFromCropTaskId] = useState('');
  const [pendingSourceGoodsTaskId, setPendingSourceGoodsTaskId] = useState('');
  const [pendingSubmitOptions, setPendingSubmitOptions] = useState({});
  const [modalIntent, setModalIntent] = useState('create');

  const toast = useCallback((message, type = 'info') => {
    setToast?.({ message, type });
  }, [setToast]);

  const openGoodsStudioCreate = useCallback((sources, options = {}) => {
    const list = Array.isArray(sources) ? sources.filter((s) => s?.id && (s.url || s.previewUrl)) : [];
    if (list.length === 0) {
      toast(i18n.t('workbench:ipeUtilError.pickImagesFirst'), 'warning');
      return;
    }
    const mapped = list.map((s) => ({
      id: String(s.id),
      name: s.name || s.id,
      url: String(s.url || s.previewUrl || '').trim(),
      previewUrl: String(s.previewUrl || s.url || '').trim(),
    }));
    setStudioParams({
      ...GOODS_STUDIO_DEFAULT_PARAMS,
      ...(options.initialParams && typeof options.initialParams === 'object' ? options.initialParams : {}),
    });
    setPendingSources(mapped);
    setPendingFromCropTaskId(
      String(options.fromCropTaskId || '').trim()
      || inferCropTaskIdFromImageIds(mapped.map((s) => s.id)),
    );
    setPendingSourceGoodsTaskId(String(options.sourceGoodsTaskId || '').trim());
    setPendingSubmitOptions({
      autoSubmit: options.autoSubmit === true,
      sourceCopywritingTaskId: String(options.sourceCopywritingTaskId || '').trim(),
      targetTaskId: String(options.targetTaskId || '').trim(),
      targetGroupIndex: Number.isFinite(Number(options.targetGroupIndex))
        ? Number(options.targetGroupIndex)
        : -1,
    });
    setModalIntent(options.intent === 'remake' ? 'remake' : 'create');
    setModalOpen(true);
  }, [toast]);

  const closeGoodsStudioCreate = useCallback(() => {
    if (busy) return;
    setModalOpen(false);
    setPendingSources([]);
    setPendingFromCropTaskId('');
    setPendingSourceGoodsTaskId('');
    setPendingSubmitOptions({});
    setModalIntent('create');
  }, [busy]);

  const confirmGoodsStudioCreate = useCallback(async (params) => {
    if (pendingSources.length === 0) {
      toast(i18n.t('workbench:ipeUtilError.pickImagesFirst'), 'warning');
      return null;
    }
    setBusy(true);
    try {
      const nextParams = { ...GOODS_STUDIO_DEFAULT_PARAMS, ...(params || studioParams || {}) };
      const prefillSources = pendingSources.map((s) => ({
        id: String(s.id),
        name: s.name || s.id,
        url: String(s.url || s.previewUrl || '').trim(),
        previewUrl: String(s.previewUrl || s.url || '').trim(),
        viewUrl: String(s.previewUrl || s.url || '').trim(),
        displayUrl: String(s.previewUrl || s.url || '').trim(),
      })).filter((s) => s.url);

      if (prefillSources.length === 0) {
        toast(i18n.t('workbench:ipeUtilError.selectImagesFirst'), 'warning');
        return null;
      }

      const sessionSourceTaskId = String(pendingFromCropTaskId || '').trim();
      if (sessionSourceTaskId) {
        const rawPrintUrlMap = {};
        prefillSources.forEach((s) => {
          rawPrintUrlMap[String(s.id)] = s.url;
        });
        const printUrlMap = buildPersistedGoodsPrintUrlMap(rawPrintUrlMap, sessionSourceTaskId);
        persistGoodsPrintUrlsToSession(sessionSourceTaskId, printUrlMap);
      }

      const sourceGoodsTaskId = String(pendingSourceGoodsTaskId || '').trim();
      const sourceCopywritingTaskId = String(
        pendingSubmitOptions.sourceCopywritingTaskId || '',
      ).trim();
      const targetTaskId = String(pendingSubmitOptions.targetTaskId || '').trim();
      const targetGroupIndex = Number(pendingSubmitOptions.targetGroupIndex);

      if (modalIntent === 'remake' && targetTaskId && targetGroupIndex >= 0) {
        const remade = await remakeGoodsStudioGroupInPlace({
          taskId: targetTaskId,
          groupIndex: targetGroupIndex,
          studioParams: nextParams,
        });
        setModalOpen(false);
        setPendingSources([]);
        setPendingFromCropTaskId('');
        setPendingSourceGoodsTaskId('');
        setPendingSubmitOptions({});
        setModalIntent('create');
        onRemakeStarted?.({
          taskId: targetTaskId,
          groupIndex: targetGroupIndex,
          result: remade.result,
          goodsForms: remade.goodsForms,
        });
        toast(i18n.t('workbench:ipeUtilError.remadeReplaced'), 'success');
        return { taskId: targetTaskId, groupIndex: targetGroupIndex, inPlace: true, ...remade };
      }

      if (pendingSubmitOptions.autoSubmit) {
        const created = await createAiGoodsTaskFromDetailSources({
          sources: prefillSources,
          studioParams: nextParams,
          fromCropTaskId: sessionSourceTaskId,
          sourceGoodsTaskId,
          sourceCopywritingTaskId,
        });
        setModalOpen(false);
        setPendingSources([]);
        setPendingFromCropTaskId('');
        setPendingSourceGoodsTaskId('');
        setPendingSubmitOptions({});
        setModalIntent('create');
        notifyQuickBridgeTaskCreated({
          created: { taskId: created.id, navId: 'goods' },
          fallbackNavId: 'goods',
          navigate,
        });
        return { taskId: created.id, count: prefillSources.length };
      }

      navigateWorkbenchWithState(navigate, 'goods', {
        openCreate: true,
        goodsStudioPrefill: {
          sources: prefillSources,
          studioParams: nextParams,
          ...(sessionSourceTaskId ? { fromCropTaskId: sessionSourceTaskId } : {}),
          ...(sourceGoodsTaskId ? { sourceGoodsTaskId } : {}),
          ...(sourceCopywritingTaskId ? { sourceCopywritingTaskId } : {}),
        },
      });

      setModalOpen(false);
      setPendingSources([]);
      setPendingFromCropTaskId('');
      setPendingSourceGoodsTaskId('');
      setPendingSubmitOptions({});
      setModalIntent('create');
      toast(i18n.t('workbench:ipeUtilError.broughtImagesToStudio', { count: prefillSources.length }), 'success');
      return { count: prefillSources.length };
    } catch (error) {
      console.error('带入主图创作失败', error);
      toast(
      error?.message || i18n.t('workbench:ipeUtilError.bringMainImageFailed'),
      'error',
    );
      return null;
    } finally {
      setBusy(false);
    }
  }, [
    navigate,
    pendingFromCropTaskId,
    pendingSourceGoodsTaskId,
    pendingSources,
    pendingSubmitOptions.autoSubmit,
    pendingSubmitOptions.sourceCopywritingTaskId,
    pendingSubmitOptions.targetTaskId,
    pendingSubmitOptions.targetGroupIndex,
    modalIntent,
    onRemakeStarted,
    studioParams,
    toast,
  ]);

  return {
    goodsStudioBusy: busy,
    goodsStudioModalOpen: modalOpen,
    goodsStudioModalIntent: modalIntent,
    goodsStudioParams: studioParams,
    setGoodsStudioParams: setStudioParams,
    goodsStudioSourceCount: pendingSources.length,
    goodsStudioSources: pendingSources,
    openGoodsStudioCreate,
    closeGoodsStudioCreate,
    confirmGoodsStudioCreate,
    /** @deprecated 使用 openGoodsStudioCreate */
    openGoodsTemplatePicker: openGoodsStudioCreate,
    /** @deprecated 使用 closeGoodsStudioCreate */
    closeGoodsTemplatePicker: closeGoodsStudioCreate,
    /** @deprecated 使用 confirmGoodsStudioCreate */
    confirmGoodsTemplates: confirmGoodsStudioCreate,
    /** @deprecated 使用 goodsStudioModalOpen */
    goodsTemplatePickerOpen: modalOpen,
    goodsTemplateBusy: busy,
  };
}
